import { useMemo } from 'react';
import { kebabCase, flatten } from 'lodash';
import { getSummary } from '@/pages/CompanyReport/redux/selectors';
import { useSelector } from 'react-redux';
import type { PrimaryCountryISOElseGlobal } from '@/constants/countries';
import { useGridCompanies } from '@/hooks/useGridCompanies';
import { updateRule } from '@ducks/grid/utils';
import { useGridView } from '@/hooks/useGridView';
import { getTop4Companies } from './utils';

type NextStepInfoParams = {
  gridViewOptionId: string;
  section: string;
};

const useNextStepInfo = ({ gridViewOptionId, section }: NextStepInfoParams) => {
  const reduxSummary = useSelector((state) => getSummary(state, {}));
  const market =
    reduxSummary.countryISO.toLowerCase() as PrimaryCountryISOElseGlobal;
  const industry =
    section === 'dividend'
      ? ''
      : kebabCase(reduxSummary.industryData.primaryName);
  const name = reduxSummary.name;
  const {
    data: { rules },
    status: gridRulesStatus,
  } = useGridView({ id: gridViewOptionId });

  const updatedRules = updateRule(rules)(market, industry);

  const { data, status: gridCompaniesStatus } = useGridCompanies({
    rules: updatedRules,
    pageQuery: 1,
    pageSize: 5,
    isInfinite: false,
  });

  const companies = useMemo(
    () => flatten(data.pages.map((page) => page.companies)),
    [data]
  );

  const top4Companies = getTop4Companies(companies, reduxSummary.companyId);

  return {
    companies: top4Companies,
    gridRulesStatus,
    gridCompaniesStatus,
    name,
    market,
    industry,
    companyId: reduxSummary.companyId,
  };
};

export default useNextStepInfo;
